import React, { useRef, useState } from 'react';
import { Box, IconButton, Chip, Tooltip, useTheme, Portal } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

interface TalentImageCarouselProps {
    images: string[];
    name: string;
    onZoomClick?: () => void;
    scrollContainerRef?: React.RefObject<HTMLElement>;
    onClick?: VoidFunction;
    isMobile?: boolean;
}

const MAX_IMAGES = 8;

const getAbsoluteRect = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    return {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
    };
};

const TalentImageCarousel: React.FC<TalentImageCarouselProps> = ({ images, name, onClick, isMobile = false }) => {
    const osTheme = useTheme();
    const [scrollIndex, setScrollIndex] = useState(0);
    const [zoomed, setZoomed] = useState(false);
    const [scale, setScale] = useState(1);
    const { themeStyle } = useThemeContext();

    const scrollRef = useRef<HTMLDivElement>(null);
    const zoomScrollRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const carouselHeight = isMobile ? 150 : 320;

    const handleScroll = (el: HTMLDivElement | null) => {
        if (!el) return;
        const { scrollLeft, offsetWidth } = el;
        const idx = Math.round(scrollLeft / offsetWidth);
        setScrollIndex(idx);
    };

    const openZoom = () => {
        // Disable zoom on mobile
        if (isMobile) return;

        if (rootRef.current) {
            rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setZoomed(true);
            setScale(1);
            requestAnimationFrame(() => setScale(1.5));
        }
    };

    const closeZoom = () => {
        setScale(1);
        setTimeout(() => {
            setZoomed(false);
        }, 400);
    };

    const scrollToIndex = (idx: number) => {
        const targetRef = zoomed ? zoomScrollRef.current : scrollRef.current;
        if (!targetRef) return;
        targetRef.scrollTo({
            left: idx * targetRef.offsetWidth,
            behavior: 'smooth',
        });
    };

    const visibleImages = images.slice(0, MAX_IMAGES);

    const ZoomedCarousel = zoomed && (
        <Portal>
            <Box
                sx={() => {
                    if (!rootRef.current) return {};
                    const cardRect = getAbsoluteRect(rootRef.current);
                    const baseWidth = cardRect.width;
                    const baseHeight = cardRect.height;

                    const left = cardRect.left + baseWidth / 2 - (baseWidth * scale) / 2;
                    const top = cardRect.top + baseHeight / 2 - (baseHeight * scale) / 2;

                    return {
                        position: 'absolute',
                        left,
                        top,
                        width: baseWidth * scale,
                        height: baseHeight * scale,
                        zIndex: 1400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition:
                            'left 0.4s cubic-bezier(.4,2,.6,1), top 0.4s cubic-bezier(.4,2,.6,1), width 0.4s cubic-bezier(.4,2,.6,1), height 0.4s cubic-bezier(.4,2,.6,1)',
                    };
                }}
                onMouseLeave={closeZoom}>
                <Box
                    ref={zoomScrollRef}
                    onScroll={() => handleScroll(zoomScrollRef.current)}
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        width: '100%',
                        height: '100%',
                        boxShadow: '0 8px 32px 8px rgba(0,0,0,0.25)',
                        borderRadius: '16px 16px 0 0',
                        background: '#222',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}>
                    {visibleImages.map((img, idx) => (
                        <Box
                            key={img + idx}
                            sx={{
                                flex: '0 0 100%',
                                width: '100%',
                                height: carouselHeight * scale,
                                scrollSnapAlign: 'start',
                                position: 'relative',
                                transition: 'height 0.4s cubic-bezier(.4,2,.6,1)',
                            }}>
                            <img
                                src={img}
                                alt={name}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '16px 16px 0 0',
                                }}
                            />
                            {idx === MAX_IMAGES - 1 && images.length > MAX_IMAGES && (
                                <Chip
                                    label={`+${images.length - MAX_IMAGES}`}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 12 * scale,
                                        right: 12 * scale,
                                        bgcolor: 'rgba(0,0,0,0.7)',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: (isMobile ? 10 : 12) + (scale - 1) * 6,
                                        transition: 'all 0.4s cubic-bezier(.4,2,.6,1)',
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 16 * scale + 8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: scale > 1 ? 2 : 1,
                        alignItems: 'center',
                        zIndex: 20,
                    }}>
                    {visibleImages.map((_, idx) => (
                        <Box
                            key={idx}
                            onClick={() => scrollToIndex(idx)}
                            sx={{
                                width: (isMobile ? 8 : 10) * scale,
                                height: (isMobile ? 8 : 10) * scale,
                                borderRadius: '50%',
                                bgcolor:
                                    idx === scrollIndex ? theme[themeStyle].highlight : theme[themeStyle].true_white,
                                border: `${scale > 1 ? 2 : 1.5}px solid`,
                                borderColor: idx === scrollIndex ? theme[themeStyle].highlight : '#bbb',
                                cursor: 'pointer',
                                transition: 'all 0.4s',
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </Portal>
    );

    return (
        <Box ref={rootRef} sx={{ position: 'relative', width: '100%', height: carouselHeight, cursor: 'pointer' }}>
            <Box
                ref={scrollRef}
                onScroll={() => handleScroll(scrollRef.current)}
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    width: '100%',
                    height: '100%',
                    borderRadius: '16px 16px 0 0',
                    background: '#222',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}>
                {visibleImages.map((img, idx) => (
                    <Box
                        key={img + idx}
                        sx={{
                            flex: '0 0 100%',
                            width: '100%',
                            height: carouselHeight,
                            scrollSnapAlign: 'start',
                            position: 'relative',
                        }}>
                        <img
                            src={img}
                            alt={name}
                            onClick={onClick}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '16px 16px 0 0',
                            }}
                        />
                        {idx === MAX_IMAGES - 1 && images.length > MAX_IMAGES && (
                            <Chip
                                label={`+${images.length - MAX_IMAGES}`}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    bottom: isMobile ? 8 : 12,
                                    right: isMobile ? 8 : 12,
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: isMobile ? 10 : 12,
                                }}
                            />
                        )}
                    </Box>
                ))}
            </Box>

            {!isMobile && (
                <Tooltip title="Zoom">
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: '#fff',
                            boxShadow: 1,
                            zIndex: 30,
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: osTheme.palette.grey[200] },
                        }}
                        size="small"
                        onClick={openZoom}>
                        <ZoomInIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            )}

            <Box
                sx={{
                    position: 'absolute',
                    bottom: isMobile ? 12 : 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    zIndex: 20,
                }}>
                {visibleImages.map((_, idx) => (
                    <Box
                        key={idx}
                        onClick={() => scrollToIndex(idx)}
                        sx={{
                            width: isMobile ? 8 : 10,
                            height: isMobile ? 8 : 10,
                            borderRadius: '50%',
                            bgcolor: idx === scrollIndex ? theme[themeStyle].highlight : theme[themeStyle].true_white,
                            border: '1.5px solid',
                            borderColor: idx === scrollIndex ? theme[themeStyle].highlight : '#bbb',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                        }}
                    />
                ))}
            </Box>

            {zoomed && ZoomedCarousel}
        </Box>
    );
};

export default TalentImageCarousel;
